import { FlowSessionRoutes, Redirect, useQuery } from '@betterment/js-runtime';
import { Helmet } from 'react-helmet';
import { Navigate, Route, Routes } from 'react-router-dom';

import AccountRouter from '../Checking/components/AccountRouter';
import CustomerLoader from '../Checking/components/CustomerLoader';
import { CustomerResponseProvider } from '../Checking/components/CustomerResponseProvider';
import ExternalAccountCreationRedirect from '../Checking/components/ExternalAccountCreationRedirect';
import { InferLayout } from '../Checking/components/InferLayout';
import NoFeeCheckingSplash from '../Checking/components/NoFeeCheckingSplash/Controller';
import NotFound from '../Checking/components/NotFound';
import { RedirectToAccount } from '../Checking/components/RedirectToAccount';
import Splash from '../Checking/components/Splash';
import { TransactionsProvider } from '../Checking/components/TransactionsContext';
import { CHECKING_DEFAULT_TITLE, CHECKING_TITLE_TEMPLATE } from '../Checking/constants';
import BankDocumentsProvider from '../Checking/contexts/BankDocumentsProvider';
import UserProvider from '../Checking/contexts/UserProvider';
import { User } from '../Checking/data';
import useCheckingGuard from '../Checking/useCheckingGuard';
import CheckingAccountCreation from '../CheckingAccountCreation';
import CheckingCardSetup from '../CheckingCardSetup';
import CheckingCustomerSignup from '../CheckingCustomerSignup';
import CheckingJointInvitationAcceptance from '../CheckingJointInvitationAcceptance';
import CheckingJointInvitationRequests from '../CheckingJointInvitationRequests';
import CheckingPinChange from '../CheckingPinChange';
import CheckingProspectSignupController from '../CheckingProspectSignup/Controller';
import { WithLayout } from '../RetailApp/components/WithLayout';
import { FooterProvider } from '../Shared/components/FooterProvider';
import FullPageLoader from '../Shared/components/FullPageLoader';
import { FlashProvider } from '../Shared/contexts/FlashContext';
import { addUrlParams, locationAssign } from '../Shared/path-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

const CheckingRoutes: React.FC = () => {
  const { refetch: loadUser, loading: isLoading, data: user } = useQuery<User>(urlBuilder.getUser());

  if (isLoading) {
    return (
      <InferLayout>
        <FullPageLoader />
      </InferLayout>
    );
  }

  return (
    <UserProvider user={user} reloadUser={loadUser}>
      <FooterProvider>
        <TransactionsProvider>
          <FlashProvider>
            <BankDocumentsProvider>
              <Helmet titleTemplate={CHECKING_TITLE_TEMPLATE} defaultTitle={CHECKING_DEFAULT_TITLE} />
              <CustomerResponseProvider>
                <Routes>
                  <Route
                    data-feature="checking_customer_signup"
                    path="/signups/*"
                    element={
                      <FlowSessionRoutes path="/checking/signups" routePath="">
                        <CheckingCustomerSignup />
                      </FlowSessionRoutes>
                    }
                  />
                  {/* TODO: https://app.asana.com/0/1199086572953002/1200142339969023/f */}
                  <Route
                    data-feature="checking_customer_signup"
                    path="/signup"
                    element={<Redirect to={urlBuilder.appCheckingSignups()} />}
                  />
                  <Route
                    data-feature="checking_customer_signup"
                    path="/prospect_signup/*"
                    element={<CheckingProspectSignupController />}
                  />
                  <Route
                    data-feature="checking_joint_account_management"
                    path="/joint_accounts_invitations/:jointAccountInvitationId/*"
                    element={
                      <WithLayout parts={{ sidebar: false, footer: true }} feature="checking_joint_account_management">
                        <CheckingJointInvitationAcceptance />
                      </WithLayout>
                    }
                  />
                  <Route
                    data-feature="checking_onboarding"
                    path="/about"
                    element={
                      <WithLayout parts={{ sidebar: false, footer: true }} feature="checking_onboarding">
                        <Splash />
                      </WithLayout>
                    }
                  />
                  <Route
                    data-feature="checking_onboarding"
                    path="/about_no_fee_checking"
                    element={
                      <WithLayout parts={{ sidebar: false, footer: true }} feature="checking_onboarding">
                        <NoFeeCheckingSplash />
                      </WithLayout>
                    }
                  />
                  <Route
                    data-feature="checking_profile_management"
                    path="/external_account_setup"
                    element={
                      <WithLayout parts={{ sidebar: false, footer: true }} feature="checking_profile_management">
                        <ExternalAccountCreationRedirect />
                      </WithLayout>
                    }
                  />
                  <Route
                    data-feature="unknown"
                    path="/*"
                    element={
                      <CustomerLoader>
                        <Routes>
                          <Route data-feature="unknown" index={true} element={<RedirectToAccount />} />
                          <Route data-feature="unknown" path="/accounts/:accountId/*" element={<AccountRouter />} />
                          <Route
                            data-feature="checking_card_management"
                            path="/card_setup/*"
                            element={
                              <WithLayout parts={{ sidebar: false, footer: true }} feature="checking_card_management">
                                <CheckingCardSetup />
                              </WithLayout>
                            }
                          />
                          <Route
                            data-feature="checking_card_management"
                            path="/pin_change/*"
                            element={
                              <WithLayout parts={{ sidebar: false, footer: true }} feature="checking_card_management">
                                <CheckingPinChange />
                              </WithLayout>
                            }
                          />
                          <Route
                            data-feature="checking_account_management"
                            path="/account_creation/*"
                            element={
                              <WithLayout
                                parts={{ sidebar: false, footer: true }}
                                feature="checking_account_management"
                              >
                                <CheckingAccountCreation />
                              </WithLayout>
                            }
                          />
                          <Route
                            data-feature="checking_joint_account_management"
                            path="/joint_accounts/*"
                            element={
                              <WithLayout
                                parts={{ sidebar: false, footer: true }}
                                feature="checking_joint_account_management"
                              >
                                <CheckingJointInvitationRequests />
                              </WithLayout>
                            }
                          />
                          <Route
                            data-feature="unknown"
                            path="/activate"
                            element={<Navigate to={urlBuilder.getAppCheckingCardSetup()} />}
                          />
                          <Route data-feature="unknown" path="*" element={<NotFound />} />
                        </Routes>
                      </CustomerLoader>
                    }
                  />
                </Routes>
              </CustomerResponseProvider>
            </BankDocumentsProvider>
          </FlashProvider>
        </TransactionsProvider>
      </FooterProvider>
    </UserProvider>
  );
};

const CheckingPage: React.FC = () => {
  const { isLoading, returnTo, queryParams } = useCheckingGuard();

  if (isLoading)
    return (
      <InferLayout>
        <FullPageLoader />
      </InferLayout>
    );

  if (returnTo) {
    locationAssign(addUrlParams(returnTo, queryParams));

    return (
      <InferLayout>
        <FullPageLoader />
      </InferLayout>
    );
  }

  return <CheckingRoutes />;
};

export default CheckingPage;
